import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from '../../../t';
import SettingBar from './SettingBar';
import withAuth from '../../../HOC/withAuth';
import C from '../../../colors';

const TAG_COLORS = [
  '#F777B0', '#D9883A', '#6B8FB8', '#3F8F5F', '#8B6FB8',
  '#D94F92', '#C8782A', '#4A7FA8', '#2E7A4F', '#7A5FB8',
  '#E8A0C0', '#F0C060', '#8BB0D0', '#5FA880', '#A090C8',
];

const SYSTEM_TAGS = [
  { name: '精修', kind: 'workflow', color: '#E85D75', description: '需要重点修图' },
  { name: '入册', kind: 'workflow', color: '#3A86FF', description: '用于相册排版' },
  { name: '放大', kind: 'workflow', color: '#F4A261', description: '用于相框或大尺寸输出' },
  { name: '备选', kind: 'workflow', color: '#6C757D', description: '暂时保留、尚未最终确认' },
];

function PrimaryBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '9px 16px', borderRadius: 7,
      background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
      boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function GhostBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '8px 14px', borderRadius: 7,
      background: '#fff', color: C.ink,
      border: `1px solid ${C.hair2}`, cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

const Tag = inject('userStore', 'snackbarStore')(observer(({ userStore, snackbarStore }) => {
  const { t } = useTranslation();
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [presetLoading, setPresetLoading] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [editingTag, setEditingTag] = useState(null);

  useEffect(() => {
    userStore.fetchTags({ includeArchived: true }).then(data => {
      setAllTags(data);
      setTags(data.filter(tag => tag.status !== 'archived'));
    });
  }, []);

  const handleDelete = async (id) => {
    try {
      const response = await userStore.deleteTag(id);
      const archived = response.data?.tag;
      setTags(current => current.filter(tag => tag.id !== id));
      setAllTags(current => current.map(tag => tag.id === id
        ? { ...tag, ...archived, status: 'archived' }
        : tag));
      setDeleteDialogOpen(false);
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.error || '删除标签失败', 'error');
    }
  };

  const handleSave = (data) => {
    if (editingTag) {
      userStore.updateTag(editingTag.id, data).then(updated => {
        setTags(current => current.map(tag => tag.id === updated.id ? updated : tag));
        setAllTags(current => current.map(tag => tag.id === updated.id ? updated : tag));
      });
    } else {
      userStore.createTag({ ...data, kind: 'workflow' }).then(created => {
        setTags(current => [...current, created]);
        setAllTags(current => [...current, created]);
      });
    }
    setDialogOpen(false);
    setEditingTag(null);
  };

  const handleSystemTag = async (preset) => {
    const existing = allTags.find(tag => tag.name === preset.name && tag.kind === preset.kind);
    if ((existing && existing.status !== 'archived') || presetLoading) return;
    setPresetLoading(preset.name);
    try {
      const activated = existing
        ? await userStore.restoreTag(existing.id)
        : await userStore.createTag(preset);
      setAllTags(current => {
        const found = current.some(tag => tag.id === activated.id);
        return found
          ? current.map(tag => tag.id === activated.id ? activated : tag)
          : [...current, activated];
      });
      setTags(current => current.some(tag => tag.id === activated.id)
        ? current.map(tag => tag.id === activated.id ? activated : tag)
        : [...current, activated]);
      snackbarStore.showSnackbar(existing ? `已恢复“${preset.name}”标签` : `已添加“${preset.name}”标签`);
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.error || '添加系统标签失败', 'error');
    } finally {
      setPresetLoading('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SettingBar />
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 36px' }}>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 38, lineHeight: 1, color: C.ink, letterSpacing: -0.5 }}>
              {t('Tag setting')}
            </h1>
            <div style={{ marginTop: 8, fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3 }}>
              共 {tags.length} 个标签
            </div>
          </div>
          <PrimaryBtn onClick={() => { setEditingTag(null); setDialogOpen(true); }}>{t('Create Tag')}</PrimaryBtn>
        </div>

        <section data-testid="system-tag-presets" style={{
          marginBottom: 30, padding: '20px 22px', borderRadius: 14,
          background: C.paper2, border: `1px solid ${C.hair}`,
        }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontFamily: C.serif, fontSize: 22, color: C.ink, marginBottom: 4 }}>系统推荐标签</div>
            <div style={{ fontSize: 12.5, color: C.ink3, lineHeight: 1.6 }}>
              点击即可添加到你的标签库，之后可以在订单中直接使用。
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
            {SYSTEM_TAGS.map(preset => {
              const existing = allTags.find(tag => tag.name === preset.name && tag.kind === preset.kind);
              const active = existing && existing.status !== 'archived';
              const loading = presetLoading === preset.name;
              return (
                <button
                  key={preset.name}
                  type="button"
                  data-testid={`system-tag-${preset.name}`}
                  data-status={active ? 'active' : existing ? 'archived' : 'available'}
                  disabled={active || !!presetLoading}
                  onClick={() => handleSystemTag(preset)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, minHeight: 66,
                    padding: '11px 13px', textAlign: 'left', borderRadius: 11,
                    border: `1px solid ${active ? `${preset.color}66` : C.hair2}`,
                    background: active ? `${preset.color}12` : '#fff',
                    cursor: active ? 'default' : presetLoading ? 'wait' : 'pointer',
                    opacity: presetLoading && !loading ? 0.55 : 1,
                    fontFamily: C.sans,
                  }}
                >
                  <span style={{ width: 12, height: 12, borderRadius: 6, background: preset.color, flexShrink: 0 }}/>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: C.ink }}>{preset.name}</span>
                    <span style={{ display: 'block', marginTop: 3, fontSize: 11.5, color: C.ink3, lineHeight: 1.35 }}>{preset.description}</span>
                  </span>
                  <span style={{ fontSize: 11, color: active ? preset.color : C.ink3, whiteSpace: 'nowrap' }}>
                    {loading ? '处理中…' : active ? '已添加' : existing ? '恢复' : '添加'}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {tags.map(tag => (
            <div key={tag.id} data-testid={`tag-card-${tag.id}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px 8px 10px',
              background: tag.color, color: '#fff',
              borderRadius: 20,
              fontFamily: C.sans, fontSize: 13, fontWeight: 500,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: 'rgba(255,255,255,.5)', flexShrink: 0 }}/>
              {tag.name}
              <button data-testid={`tag-edit-${tag.id}`} aria-label={`编辑标签 ${tag.name}`} onClick={() => { setEditingTag(tag); setDialogOpen(true); }}
                style={{ background: 'rgba(255,255,255,.25)', border: 'none', color: '#fff', width: 20, height: 20, borderRadius: 10, cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 14, lineHeight: 1, marginLeft: 2 }}>
                ✎
              </button>
              <button data-testid={`tag-delete-${tag.id}`} aria-label={`删除标签 ${tag.name}`} onClick={() => { setTagToDelete(tag); setDeleteDialogOpen(true); }}
                style={{ background: 'rgba(255,255,255,.25)', border: 'none', color: '#fff', width: 20, height: 20, borderRadius: 10, cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 14, lineHeight: 1 }}>
                ×
              </button>
            </div>
          ))}

          {tags.length === 0 && (
            <div style={{ padding: '60px 0', textAlign: 'center', width: '100%', color: C.ink3, fontSize: 14 }}>
              暂无标签，点击右上角创建
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      {dialogOpen && (
        <TagDialog
          tag={editingTag}
          onSave={handleSave}
          onClose={() => { setDialogOpen(false); setEditingTag(null); }}
        />
      )}

      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <div onClick={() => setDeleteDialogOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(26,22,20,.4)',
          display: 'grid', placeItems: 'center', zIndex: 9999,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.paper, borderRadius: 14, padding: '24px 28px',
            maxWidth: 400, width: '100%', border: `1px solid ${C.hair}`,
            boxShadow: '0 24px 56px -16px rgba(26,22,20,.32)',
          }}>
            <h3 style={{ margin: '0 0 8px', fontFamily: C.serif, fontSize: 22, fontWeight: 400, color: C.ink }}>{t('Delete Tag')}</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: C.ink2 }}>{t('Are you sure you want to delete this tag?')}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <GhostBtn onClick={() => setDeleteDialogOpen(false)}>{t('Cancel')}</GhostBtn>
              <button data-testid="tag-delete-confirm" onClick={() => handleDelete(tagToDelete.id)} style={{
                fontFamily: C.sans, fontSize: 13, fontWeight: 500,
                padding: '8px 16px', borderRadius: 7, border: 'none',
                background: '#C53C3C', color: '#fff', cursor: 'pointer',
              }}>{t('Delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}));

function TagDialog({ tag, onSave, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState(tag?.name || '');
  const [color, setColor] = useState(tag?.color || TAG_COLORS[0]);
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!name.trim()) { setError(t('Tag name cannot be empty.')); return; }
    onSave({ name: name.trim(), color });
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(26,22,20,.4)',
      display: 'grid', placeItems: 'center', zIndex: 9999,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.paper, borderRadius: 14, padding: '24px 28px',
        maxWidth: 420, width: '100%', border: `1px solid ${C.hair}`,
        boxShadow: '0 24px 56px -16px rgba(26,22,20,.32)',
      }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: C.serif, fontSize: 22, fontWeight: 400, color: C.ink }}>
          {tag ? t('Edit Tag') : t('Create New Tag')}
        </h3>
        {error && <div style={{ fontSize: 12, color: '#c2412e', marginBottom: 12 }}>{error}</div>}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: C.ink3, marginBottom: 4 }}>{t('Tag Name')}</div>
          <input data-testid="tag-name-input" value={name} onChange={e => setName(e.target.value)}
            placeholder={t('Tag Name')}
            style={{
              width: '100%', padding: '10px 14px',
              border: `1px solid ${!name.trim() ? '#e88c7c' : C.hair2}`, borderRadius: 8,
              fontSize: 14, fontFamily: C.sans, outline: 'none', color: C.ink,
            }}/>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: C.ink3, marginBottom: 8 }}>颜色</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TAG_COLORS.map(c => (
              <button key={c} data-testid={`tag-color-${c.slice(1)}`} aria-label={`标签颜色 ${c}`} onClick={() => setColor(c)} style={{
                width: 36, height: 36, borderRadius: 18,
                background: c, border: `2px solid ${color === c ? C.ink : 'transparent'}`,
                cursor: 'pointer', padding: 0,
              }}/>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GhostBtn onClick={onClose}>{t('Cancel')}</GhostBtn>
          <PrimaryBtn onClick={handleSave}>{t('Save')}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

export default withAuth(Tag);
